import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  Collapse,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { formatDate } from "../../../../../utils/dateUtils";
import AuditHeader from "../../components/common/AuditHeader";
import EditableField from "../../components/common/EditableField";

const AuditDetailsCard = ({
  processedAudit,
  handlers,
  updating,
  isAuditorView,
  auditorOptions,
  detailsExpanded,
  onToggleExpanded,
}) => {
  const theme = useTheme();

  // Calculate days remaining until audit end date
  const getDaysRemaining = () => {
    if (!processedAudit?.audit_end_date) return "-";

    // If audit is completed/archived, show status instead
    if (processedAudit.audit_status === "completed") {
      return "Completed";
    }
    if (processedAudit.audit_status === "archived") {
      return "Archived";
    }

    // Parse end date - handle various formats (timestamp, ISO string, etc.)
    let endDate;
    const endDateValue = processedAudit.audit_end_date;

    if (typeof endDateValue === "string" && /^\d+$/.test(endDateValue)) {
      // Unix timestamp as string
      endDate = new Date(Number(endDateValue));
    } else if (typeof endDateValue === "number") {
      // Unix timestamp
      endDate = new Date(endDateValue);
    } else {
      // ISO string or other date format
      endDate = new Date(endDateValue);
    }

    // Validate date
    if (isNaN(endDate.getTime())) {
      return "-";
    }

    // Normalize dates to midnight for comparison (without modifying original dates)
    const today = new Date();
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    // Calculate difference in days
    const diffTime = endDateNormalized - todayNormalized;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? "" : "s"}`;
    } else if (diffDays === 0) {
      return "Ends today";
    } else {
      const overdueDays = Math.abs(diffDays);
      return `${overdueDays} day${overdueDays === 1 ? "" : "s"} overdue`;
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.customColors?.border?.main}`,
        overflow: "hidden",
        mb: 3,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Collapsible Header - Clickable */}
        <Box
          onClick={onToggleExpanded}
          sx={{
            pt: 3,
            px: 3,
            pb: detailsExpanded ? 3 : 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            transition: "all 0.2s",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AuditHeader processedAudit={processedAudit} noBottomMargin={true} />
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "8px",
              transition: "all 0.3s",
              transform: detailsExpanded ? "rotate(180deg)" : "rotate(0deg)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            <ExpandMoreIcon
              sx={{
                color: theme.customColors?.text?.secondary,
                fontSize: "28px",
              }}
            />
          </Box>
        </Box>

        <Collapse in={detailsExpanded}>
          <Box sx={{ px: 3, pt: 2, mb: 2 }}>
            <Grid container spacing={4}>
              {/* Total Controls */}
              <Grid item xs={6} sm={4} md={2.5}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <ShieldIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="subtitle2">
                    Total Controls
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="600" pl={3.5}>
                  {processedAudit.total_controls || 0}
                </Typography>
              </Grid>

              {/* Audit Start Date */}
              <Grid item xs={6} sm={4} md={3.5}>
                <EditableField
                  icon={ScheduleIcon}
                  label="Audit Period Start"
                  value={formatDate(processedAudit.audit_start_date)}
                  isEditing={handlers.editingEvidenceStart}
                  tempValue={handlers.tempEvidenceStart}
                  onEdit={handlers.handleEditEvidenceStart}
                  onSave={handlers.handleSaveEvidenceStart}
                  onCancel={handlers.handleCancelEvidenceStart}
                  onChange={handlers.setTempEvidenceStart}
                  updating={updating}
                  isAuditorView={isAuditorView}
                  type="date"
                  disabled={true}
                />
              </Grid>

              {/* Audit End Date */}
              <Grid item xs={6} sm={4} md={3}>
                <EditableField
                  icon={ScheduleIcon}
                  label="Audit Period End"
                  value={formatDate(processedAudit.audit_end_date)}
                  isEditing={handlers.editingEvidenceEnd}
                  tempValue={handlers.tempEvidenceEnd}
                  onEdit={handlers.handleEditEvidenceEnd}
                  onSave={handlers.handleSaveEvidenceEnd}
                  onCancel={handlers.handleCancelEvidenceEnd}
                  onChange={handlers.setTempEvidenceEnd}
                  updating={updating}
                  isAuditorView={isAuditorView}
                  type="date"
                  disabled={true}
                />
              </Grid>

              {/* Auditor (admin) or Days Until Audit End (auditor view) */}
              <Grid item xs={12} sm={6} md={3}>
                {isAuditorView ? (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                      <Typography variant="subtitle2">
                        Days Until Audit End
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="600" pl={3}>
                      {getDaysRemaining()}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                      <Typography variant="subtitle2">
                        {processedAudit.auditors && processedAudit.auditors.length > 1 ? "Auditors" : "Auditor"}
                      </Typography>
                      {!handlers.editingAuditor && (
                        <Tooltip title={processedAudit.audit_status === "completed" ? "Cannot edit a completed audit" : "Edit auditor"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handlers.handleEditAuditor}
                              disabled={updating || processedAudit.audit_status === "completed"}
                              sx={{
                                p: 0.5,
                                color: theme.customColors?.text?.secondary,
                                "&:hover": { bgcolor: theme.customColors?.background?.light },
                                "&.Mui-disabled": {
                                  color: theme.customColors?.text?.disabled || "#D1D5DB",
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    {handlers.editingAuditor ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={auditorOptions || []}
                          value={handlers.tempAuditors || []}
                          onChange={(_, newValue) => {
                            handlers.setTempAuditors(newValue);
                          }}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            String(option.id) === String(value.id) && option.is_internal === value.is_internal
                          }
                          groupBy={(option) => (option.is_internal ? "Internal Employees" : "External Auditors")}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={`${option.id}-${option.is_internal}`}
                                label={option.name}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select auditors..."
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "0.75rem",
                                  minHeight: "32px",
                                },
                              }}
                            />
                          )}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            "& .MuiAutocomplete-tag": {
                              fontSize: "0.75rem",
                              height: "24px",
                            },
                          }}
                        />
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={handlers.handleSaveAuditor}
                            disabled={updating}
                            sx={{
                              color: "#10B981",
                              flexShrink: 0,
                              "&:hover": { bgcolor: "rgba(16, 185, 129, 0.1)" },
                            }}
                          >
                            <SaveIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handlers.handleCancelAuditor}
                            sx={{
                              color: theme.customColors?.text?.secondary,
                              flexShrink: 0,
                              "&:hover": { bgcolor: theme.customColors?.background?.light },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <>
                        {processedAudit.audit_type === "internal" && processedAudit.auditor_name === "Internal Auditing" && (!processedAudit.auditors || processedAudit.auditors.length === 0) ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontStyle: "italic",
                              color: theme.customColors.text.secondary,
                              fontSize: "0.875rem",
                              pl: 3.5,
                            }}
                          >
                            {processedAudit.auditor_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight="600" pl={3.5}>
                            {processedAudit.auditor_name || "-"}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AuditDetailsCard;
